import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './ShelfPulse.css';

function ShelfPulsePrivacy() {
  return (
    <div className="sp-page">
      <Helmet>
        <title>Privacy Policy - ShelfPulse</title>
        <meta name="description" content="Privacy Policy for ShelfPulse on iPhone, iPad, and Android. No accounts, no analytics: your Audiobookshelf data stays between your device and your server." />
        <link rel="canonical" href="https://audrabooks.app/shelfpulse/privacy" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Navigation */}
      <nav className="sp-nav">
        <div className="sp-nav-container">
          <Link to="/shelfpulse" className="sp-nav-logo">
            <svg className="sp-logo-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 7h8M8 11h8M8 15h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="sp-logo-text">ShelfPulse</span>
          </Link>
          <div className="sp-nav-links">
            <Link to="/shelfpulse" className="sp-nav-link">Home</Link>
            <Link to="/shelfpulse/terms" className="sp-nav-link">Terms of Service</Link>
          </div>
        </div>
      </nav>

      <div className="sp-legal-container">
        <Link to="/shelfpulse" className="sp-back-link">← Back to Home</Link>

        <div className="sp-legal-header">
          <h1 className="sp-legal-title">Privacy Policy</h1>
          <p className="sp-legal-updated">Last Updated: October 23, 2026</p>
        </div>

        <div className="sp-legal-content">
          <div className="sp-highlight-box">
            <h3>Your Data is Yours</h3>
            <p>ShelfPulse has no accounts, no analytics, and no servers of its own. Your Audiobookshelf credentials and data travel only between your device and your own Audiobookshelf server. We never see them.</p>
          </div>

          <p>This Privacy Policy explains how ShelfPulse ("we," "us," or "our") handles information when you use the ShelfPulse app for iPhone, iPad, and Android ("the App"). ShelfPulse is a companion app for Audiobookshelf, a self-hosted audiobook and podcast server.</p>

          <h2>1. What We Collect</h2>
          <p>Nothing. We do not run servers for ShelfPulse, and the App contains no analytics, advertising, tracking, or crash-reporting software. We do not collect, store, sell, or have access to:</p>
          <ul>
            <li>Your Audiobookshelf server address, username, password, or tokens</li>
            <li>Your listening history, statistics, or library contents</li>
            <li>Information about other users on your server</li>
            <li>Your location, contacts, or device identifiers</li>
          </ul>

          <h2>2. How the App Works</h2>
          <ul>
            <li><strong>Direct connection:</strong> the App talks directly from your device to the Audiobookshelf server you choose. There is no ShelfPulse server in between.</li>
            <li><strong>Stored on your device:</strong> your server address and sign-in tokens, cached statistics and listening sessions, book covers, widget data, and your settings are kept on your device so the App opens quickly.</li>
            <li><strong>Signing out</strong> removes your tokens and the cached data for that sign-in from the device.</li>
          </ul>

          <h2>3. How Your Sign-In Is Protected</h2>
          <ul>
            <li><strong>iPhone and iPad:</strong> tokens are stored in the iOS Keychain, encrypted and kept on this device only. They are not synced through iCloud.</li>
            <li><strong>Android:</strong> tokens are stored encrypted with a key held in the Android Keystore. They are excluded from Android cloud backups and device-to-device transfers, so a restored phone has to sign in again.</li>
            <li>We never store your password. It is sent once to your server when you sign in, and your server returns a token.</li>
          </ul>

          <h2>4. If You Manage a Server</h2>
          <p>If you sign in as an Audiobookshelf administrator, the App shows information your server already holds about its users: who is listening, what they listened to and for how long, the device and app they used, and the IP address your server recorded for each session. The App can also change data on your server, such as books, users, libraries, collections, API keys, and backups, when you choose to.</p>
          <p>That information belongs to you and your server. ShelfPulse only displays it on your device and does not send it anywhere, except as described in section 5. As the server's administrator, you are responsible for how you use it and for telling the people on your server how their data is handled.</p>
          <p>When you create a user or set a temporary password, the App can open your device's share sheet with a sign-in message. It is sent only to whoever you choose, through the app you pick.</p>

          <h2>5. Third-Party Services the App Contacts</h2>
          <ul>
            <li><strong>Your Audiobookshelf server:</strong> every request goes to the server you entered.</li>
            <li><strong>ip-api.com:</strong> when an administrator opens a listening session's details, the App sends that session's public IP address to ip-api.com to show an approximate city and country. Private network addresses are never sent. This request uses an unencrypted connection, and ip-api.com's own privacy policy applies to it.</li>
            <li><strong>GitHub:</strong> to tell administrators when a new Audiobookshelf version is out, the App asks GitHub's public API for the latest Audiobookshelf release. No information about you or your server is included.</li>
            <li><strong>Apple App Store and Google Play:</strong> purchases and subscriptions are handled entirely by Apple or Google. We never see your payment details.</li>
          </ul>
          <p>Book searches, matches, cover lookups, and chapter lookups are carried out by your Audiobookshelf server, not by the App.</p>

          <h2>6. Apple and Google</h2>
          <ul>
            <li><strong>iCloud (iPhone and iPad):</strong> one display setting for collections may sync across your Apple devices through iCloud. Your sign-in, data, and other settings are not synced.</li>
            <li><strong>Crash reports:</strong> if you have chosen to share diagnostics with Apple or Google in your device settings, they may share anonymous crash reports with us. These never include data from your Audiobookshelf server.</li>
            <li>These services are covered by <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">Apple's Privacy Policy</a> and <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</li>
          </ul>

          <h2>7. Support Emails and Logs</h2>
          <p>The App keeps a log of the last 24 hours of its own activity on your device to help diagnose problems. It is never sent automatically. If you choose "Send Logs" in Contact Support, your email app opens with the log attached, along with your app version, operating system version, and device model. The log contains technical details such as requests and errors, which can include your server's address and usernames, but never passwords or tokens. Anything you email us is used only to help you and is deleted when it is no longer needed.</p>

          <h2>8. Your Choices</h2>
          <ul>
            <li><strong>Sign out</strong> to remove your sign-in and cached data from the device.</li>
            <li><strong>Clear Cache</strong> in Settings removes cached statistics and history.</li>
            <li><strong>Delete the App</strong> to remove everything it stored.</li>
            <li>Data on your Audiobookshelf server is controlled by your server's administrator, not by ShelfPulse.</li>
          </ul>

          <h2>9. Children</h2>
          <p>ShelfPulse is not directed to children under 13, and we do not knowingly collect information from anyone.</p>

          <h2>10. International Users, GDPR, and California</h2>
          <p>Because we do not collect personal information, we do not process, transfer, sell, or share it. Under the GDPR and the California Consumer Privacy Act you still have the right to ask us what we hold about you: the answer is nothing beyond any emails you have sent us, which we will delete on request.</p>

          <h2>11. Changes to This Policy</h2>
          <p>We will post any changes on this page and update the date at the top. For a material change we will also note it in the App's What's New.</p>

          <h2>12. Contact Us</h2>
          <p>
            <strong>Eric Randall</strong><br />
            Email: hello@audrabooks.app<br />
            Address: 25 S 200 W, Hyde Park, UT 84318<br />
            United States
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="sp-footer">
        <div className="sp-footer-container">
          <div className="sp-footer-content">
            <div className="sp-footer-brand">
              <div className="sp-footer-logo">
                <svg className="sp-logo-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="sp-logo-text">ShelfPulse</span>
              </div>
              <p className="sp-footer-tagline">Your Audiobook Dashboard</p>
            </div>
          </div>
          <div className="sp-footer-bottom">
            <p>&copy; {new Date().getFullYear()} ShelfPulse. All rights reserved.</p>
            <div className="sp-footer-links">
              <Link to="/shelfpulse/terms">Terms of Service</Link>
              <Link to="/shelfpulse/privacy">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ShelfPulsePrivacy;
